import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError, errorText } from '../api';
import { GeloeschtHinweis } from '../billtoro/GeloeschtHinweis';
import { LegalLinks } from '../components/LegalLinks';
import { LangSwitch, t, tn } from '../i18n';
import { useConfig } from '../lib/config';
import { isBilltoro, markenName } from '../produkt';
import { afterLogin } from './redirect';
import { useSession } from './session';

// Antwort auf das Passwort, wenn noch der Login-Code per E-Mail nötig ist (sonst 204 ohne Inhalt)
interface CodeStep {
  code: true;
  email: string;
}

const RESEND_SECONDS = 30;
// Ohne gültigen Code geht es nur mit dem Passwort von vorn weiter
const RESTART = ['code_missing', 'code_expired', 'code_locked', 'too_many', 'login_locked'];

export function LoginPage() {
  const { refresh, ended } = useSession();
  const { idleMinutes, signup } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Schritt 2: Code aus der E-Mail (sentTo = Adresse, teils verdeckt)
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function enter() {
    const me = await refresh();
    // zurück auf die vorher aufgerufene Seite (z. B. Link aus einer E-Mail), sonst Startseite
    if (me) navigate(afterLogin(location.state, me), { replace: true });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    // Leere Felder gar nicht erst schicken (sonst klingt es nach falschen Angaben)
    if (!email.trim() || !password) {
      setError(t('Bitte E-Mail und Passwort eingeben.'));
      document.getElementById(email.trim() ? 'lg-pw' : 'lg-mail')?.focus();
      return;
    }
    setBusy(true);
    try {
      const res = await api.post<CodeStep | undefined>('/api/auth/login', { email, password });
      if (res?.code) setSentTo(res.email);
      else await enter();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (sentTo) {
    return (
      <CodeForm
        sentTo={sentTo}
        onDone={enter}
        onRestart={(msg) => {
          setSentTo(null);
          setPassword('');
          setError(msg);
        }}
      />
    );
  }

  return (
    <div className="auth">
      <div className="auth-box">
        <div className="auth-brand">
          <span className="logo" role="img" aria-label={markenName()} />
          <LangSwitch />
        </div>
        <h1>{t('Anmelden')}</h1>
        <p>{t('Mit der E-Mail und dem Passwort, die Sie bei der Registrierung gewählt haben.')}</p>
        {isBilltoro() && <GeloeschtHinweis />}
        {ended === 'idle' && (
          <p className="notice info" role="status">
            {idleMinutes
              ? t('Aus Sicherheitsgründen wurden Sie nach {n} Minuten ohne Aktivität abgemeldet. Bitte melden Sie sich neu an.', {
                  n: idleMinutes,
                })
              : t(
                  'Aus Sicherheitsgründen wurden Sie nach längerer Zeit ohne Aktivität abgemeldet. Bitte melden Sie sich neu an.',
                )}
          </p>
        )}
        <form className="card" onSubmit={submit} noValidate>
          <label className="field">
            <span>{t('E-Mail')}</span>
            <input
              id="lg-mail"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              aria-describedby={error ? 'lg-err' : undefined}
            />
          </label>
          <label className="field">
            <span>{t('Passwort')}</span>
            <input
              id="lg-pw"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              aria-describedby={error ? 'lg-err' : undefined}
            />
          </label>
          {error && (
            <p className="notice bad" role="alert" id="lg-err">
              {error}
            </p>
          )}
          <button className="btn primary block" disabled={busy}>
            {busy ? t('Einen Moment …') : t('Anmelden')}
          </button>
        </form>
        <p className="auth-foot">
          <Link to="/passwort-vergessen">{t('Passwort vergessen?')}</Link>
        </p>
        {isBilltoro() ? (
          signup && (
            <p className="auth-foot">
              Noch kein Konto? <Link to="/registrieren">Kostenlos registrieren</Link>
            </p>
          )
        ) : (
          <p className="auth-foot">
            {tn('Noch kein Zugang? {link}', { link: <Link to="/registrieren">{t('Jetzt registrieren')}</Link> })}
          </p>
        )}
        <LegalLinks mitAgb />
      </div>
    </div>
  );
}

// Schritt 2: Login-Code aus der E-Mail. „Code erneut senden“ geht nach 30 Sekunden.
function CodeForm({
  sentTo,
  onDone,
  onRestart,
}: {
  sentTo: string;
  onDone(): Promise<void>;
  onRestart(msg: string | null): void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(RESEND_SECONDS);

  useEffect(() => {
    if (wait <= 0) return;
    const timer = window.setTimeout(() => setWait((w) => w - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [wait]);

  function failed(err: unknown) {
    if (err instanceof ApiError && RESTART.includes(err.code ?? '')) onRestart(errorText(err));
    else {
      setInfo(null);
      setError(errorText(err));
      document.getElementById('lg-code')?.focus();
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (code.length !== 6) {
      setError(t('Bitte den 6-stelligen Code aus der E-Mail eingeben.'));
      document.getElementById('lg-code')?.focus();
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/auth/login/code', { code });
      await onDone();
    } catch (err) {
      failed(err);
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setError(null);
    try {
      await api.post('/api/auth/login/resend');
      setCode('');
      setInfo(t('Neuer Code an {email} verschickt.', { email: sentTo }));
      setWait(RESEND_SECONDS);
    } catch (err) {
      failed(err);
    }
  }

  return (
    <div className="auth">
      <div className="auth-box">
        <div className="auth-brand">
          <span className="logo" role="img" aria-label={markenName()} />
          <LangSwitch />
        </div>
        <h1>{t('Code eingeben')}</h1>
        <p>{tn('Wir haben Ihnen einen Code an {email} geschickt. Bitte geben Sie ihn hier ein.', { email: <b>{sentTo}</b> })}</p>
        <form className="card" onSubmit={submit} noValidate>
          <label className="field">
            <span>{t('Code aus der E-Mail')}</span>
            <input
              id="lg-code"
              className="code-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              aria-describedby={error ? 'lg-err' : 'lg-code-h'}
            />
            <small id="lg-code-h">{t('Der Code gilt 10 Minuten. Keine E-Mail da? Bitte auch im Spam-Ordner nachsehen.')}</small>
          </label>
          {error && (
            <p className="notice bad" role="alert" id="lg-err">
              {error}
            </p>
          )}
          {info && (
            <p className="notice ok" role="status">
              {info}
            </p>
          )}
          <button className="btn primary block" disabled={busy}>
            {busy ? t('Einen Moment …') : t('Anmelden')}
          </button>
        </form>
        <p className="auth-foot">
          <button type="button" className="linkbtn" disabled={wait > 0} onClick={resend}>
            {wait > 0 ? t('Code erneut senden (in {n} s)', { n: wait }) : t('Code erneut senden')}
          </button>
        </p>
        <p className="auth-foot">
          <button type="button" className="linkbtn" onClick={() => onRestart(null)}>
            {t('Zurück zur Anmeldung')}
          </button>
        </p>
        <LegalLinks mitAgb />
      </div>
    </div>
  );
}
